<script setup lang="ts" generic="TData">
import { FlexRender, type Row, type HeaderGroup } from '@tanstack/vue-table';

import { AppSkeleton } from '@/shared/ui/skeleton';

import type { EmptyState } from '../types';

interface Props {
  loading: boolean;
  isEmpty: boolean;
  emptyState: EmptyState;
  rows: Row<TData>[];
  headerGroups: HeaderGroup<TData>[];
  getColumnStyle: (headerId: string) => Record<string, string> | undefined;
  serverSide: boolean;
  hasMore: boolean;
  fetchingMore: boolean;
  rowClickable: boolean;
  rowClass?: (row: TData) => string | undefined;
  rowDivider?: (row: TData, previous?: TData) => string | undefined;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'row-click': [row: TData, event: MouseEvent | KeyboardEvent];
  'row-hover': [row: TData, event: MouseEvent];
}>();

defineSlots<{
  empty?: () => unknown;
  [key: `cell-${string}`]: (props: { cell: unknown; row: unknown; value: unknown }) => unknown;
}>();

const TEXT_ALIGN = { center: 'text-center', end: 'text-right' } as const;

const ROW_STAGGER_LIMIT = 8;
const ROW_STAGGER_MS = 25;

const handleRowClick = (row: TData, event: MouseEvent | KeyboardEvent) => {
  emit('row-click', row, event);
};

const dividerOf = (rowIndex: number): string | undefined => {
  const row = props.rows[rowIndex];

  return row ? props.rowDivider?.(row.original, props.rows[rowIndex - 1]?.original) : undefined;
};

const handleRowHover = (row: TData, event: MouseEvent) => {
  emit('row-hover', row, event);
};
</script>

<template>
  <tbody class="base-table__tbody">
    <!-- Loading state -->
    <template v-if="loading">
      <tr
        v-for="rowIndex in 5"
        :key="`skeleton-${rowIndex}`"
        class="base-table__skeleton-row"
      >
        <td
          v-for="header in headerGroups[0]?.headers || []"
          :key="`skeleton-cell-${rowIndex}-${header.id}`"
          :style="getColumnStyle(header.id)"
          class="base-table__td"
        >
          <AppSkeleton
            :width="`${60 + Math.random() * 35}%`"
            height="1rem"
            variant="text"
          />
        </td>
      </tr>
    </template>

    <!-- Empty state -->
    <tr
      v-else-if="isEmpty"
      class="base-table__empty-row"
    >
      <td
        :colspan="headerGroups[0]?.headers.length || 1"
        class="base-table__empty-cell"
      >
        <slot name="empty">
          <div class="base-table__empty-content">
            <p class="base-table__empty-title">{{ emptyState.title }}</p>
            <p
              v-if="emptyState.description"
              class="base-table__empty-description"
            >
              {{ emptyState.description }}
            </p>
          </div>
        </slot>
      </td>
    </tr>

    <!-- Data rows -->
    <template v-else>
      <!-- Строки въезжают при появлении: после скелетона и при догрузке; лесенка ограничена восемью шагами. -->
      <template
        v-for="(row, rowIndex) in rows"
        :key="row.id"
      >
        <tr v-if="dividerOf(rowIndex)">
          <td
            :colspan="headerGroups[0]?.headers.length || 1"
            class="base-table__divider-cell"
          >
            {{ dividerOf(rowIndex) }}
          </td>
        </tr>
        <tr
          :style="{ animationDelay: `${Math.min(rowIndex, ROW_STAGGER_LIMIT) * ROW_STAGGER_MS}ms` }"
          :class="[
            'base-table__row motion-safe:animate-content-in',
            rowClass?.(row.original),
            {
              'base-table__row--selected': row.getIsSelected(),
              'base-table__row--clickable': rowClickable,
            },
          ]"
          :tabindex="rowClickable ? 0 : undefined"
          @click="handleRowClick(row.original, $event)"
          @keydown.enter.self="rowClickable && handleRowClick(row.original, $event)"
          @mouseenter="handleRowHover(row.original, $event)"
        >
          <td
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            :style="getColumnStyle(cell.column.id)"
            class="base-table__td"
            :class="cell.column.columnDef.meta?.align && TEXT_ALIGN[cell.column.columnDef.meta.align]"
          >
            <!-- Custom cell slot -->
            <slot
              v-if="$slots[`cell-${cell.column.id}`]"
              :name="`cell-${cell.column.id}`"
              :cell="cell"
              :row="row"
              :value="cell.getValue()"
            />
            <!-- Default cell -->
            <FlexRender
              v-else
              :render="cell.column.columnDef.cell"
              :props="cell.getContext()"
            />
          </td>
        </tr>
      </template>
    </template>

    <!-- Infinity scroll sentinel -->
    <tr
      v-if="serverSide && hasMore && !isEmpty"
      class="base-table__sentinel"
    >
      <td
        v-for="header in headerGroups[0]?.headers || []"
        :key="`sentinel-${header.id}`"
        :style="getColumnStyle(header.id)"
        class="base-table__td"
      >
        <AppSkeleton
          v-if="fetchingMore"
          :width="`${60 + Math.random() * 35}%`"
          height="1rem"
          variant="text"
        />
      </td>
    </tr>
  </tbody>
</template>

<style scoped>
@reference "../../../../tailwind.css";

@layer components {
  .base-table__tbody {
    color: var(--text-primary);
  }

  .base-table__row {
    border-bottom: 1px solid var(--border);
  }

  .base-table__row:hover {
    background-color: var(--surface-hover);
  }

  .base-table__row--clickable {
    @apply cursor-pointer;
  }

  .base-table__row--selected {
    background-color: var(--surface-sunken);
  }

  .base-table__td {
    @apply px-4 py-3 text-sm;
  }

  .base-table__skeleton-row {
    border-bottom: 1px solid var(--border);
  }

  .base-table__empty-cell {
    @apply px-4 py-8 text-center;
  }

  .base-table__empty-content {
    @apply flex flex-col items-center gap-2;
  }

  .base-table__empty-title {
    @apply text-base font-medium;
    color: var(--text-primary);
  }

  .base-table__empty-description {
    @apply text-sm;
    color: var(--text-secondary);
  }

  .base-table__divider-cell {
    @apply px-4 pt-5 pb-2 text-xs font-medium;
    color: var(--text-secondary);
  }

  .base-table__sentinel {
    @apply border-none;
  }
}
</style>
